import Axios from "axios";
import { List } from "../@types/list";
import { IGroupToken } from "../Groups/controller";
import { RequestPsychologistPromotionParams } from "../ResellerClientsManagement/controllers/ResellerManagementController";
import { environment } from "../utils/environment";

export type ApiPurchaseUser = {
  identification: string;
  cnpj?: string;
  birthDate: {
    day: number;
    month: number;
    year: number;
  };
  contact: {
    type: "HOME" | "MOBILE" | "WORK";
    phoneNumber: string;
  };
};

export type UserProfile = {
  identification: string;
  identificationType: "CPF" | "CNPJ";
  birthDate: string;
  contact: {
    type: "HOME" | "MOBILE" | "WORK";
    phoneNumber: string;
  };
  updatedAt?: string;
};

export type UpdateUserProfilePayload = {
  identification: string;
  identificationType: "CPF" | "CNPJ";
  birthDate: string;
  contact: {
    type: "MOBILE";
    phoneNumber: string;
  };
};

export type ApiPurchasePayment = {
  type: "PIX" | "CREDIT_CARD";
  fingerprint: string;
  creditCardInfo?: {
    issuer: "MASTERCARD" | "VISA" | "AMEX" | "ELO" | "HIPERCARD" | "HIPER";
    cardName: string;
    cardNumber: string;
    cardExpirationMonth: string;
    cardExpirationYear: string;
    cardCvv: string;
    installments?: number;
  };
};

export interface IInstrument {
  id: number;
  title: string;
  description: string;
  priceInBrlCents: number;
  quantity: number;
  discountRules?: InstrumentDiscount[];
}

export interface InstrumentDiscount {
  id?: number;
  minQty: number;
  maxQty: number | null;
  discountPct: number;
  unitPriceOverrideCents: number | null;
  discountedUnitPriceCents?: number | null;
}

export type GetManagedInstrumentsSearchParams = {
  page: number;
  limit: number;
  filter?: string;
};

export type getClientsParams = Partial<GetManagedInstrumentsSearchParams> & {
  origin?: "INVITED_NEW_USER" | "EXISTING_USER_LINKED";
  invitationStatus?: "PENDING" | "ACCEPTED";
};

export type SearchAllUsersParams = {
  page?: number;
  limit?: number;
  filter?: string;
  roles?: string[];
  isPsychologist?: boolean;
};

export interface IUserResponse {
  firstName: string;
  lastName: string;
  uid: string;
  email: string;
  is_psychologist?: boolean;
  cpf?: string;
  psychologist_verified?: boolean;
  roles?: string[];
}

export type GetLicenseHistoryParams = {
  page?: number;
  limit?: number;
  filter?: string;
  clientId?: number;
  instrumentId?: number;
};

export interface IAddOrInviteResellerClientParams {
  email: string;
  firstName: string;
  lastName: string;
  isPsychologist?: boolean;
  cpf?: string;
  crp?: string;
}

export interface LicenseAssignmentItem {
  instrumentId: number;
  quantity: number;
}

export interface PaginationParams {
  page: number;
  limit: number;
  filter?: string;
}

export type RoleTranslations = Record<string, string>;

export interface PromoteToPsychologistParams {
  cpf?: string;
  crp?: string;
  situacao?: string | null;
  regiao?: string | null;
  dataInsc?: string | null;
}

export type LicenseEventType = "ASSIGN" | "REVOKE";

export type GetLicenseEventsParams = {
  page?: number;
  limit?: number;
  order?: "asc" | "desc";
  clientId?: number;
  instrumentId?: number;
  from?: Date | string;
  to?: Date | string;
};

export type LicenseEventDTO = {
  id: number;
  eventType: LicenseEventType;
  quantity: number;
  prevQuantityAssigned: number;
  newQuantityAssigned: number;
  createdAt: string;
};

export type CartItemResponse = {
  id: number;
  instrumentId: number;
  title: string;
  quantity: number;
  unitPriceCents: number;
  subtotalCents: number;
  discountedUnitPriceCents: number;
  discountedSubtotalCents: number;
  totalDiscountCents: number;
  appliedDiscountPct: number | null;
  priceSnapshotCents: number | null;
  priceChanged: boolean;
};

export type CouponDiscountType = "percent" | "fixed_amount";

export type AppliedCouponResponse = {
  code: string;
  discountType: CouponDiscountType;
  percentOff: number | null;
  amountOffCents: number | null;
  minOrderAmountCents: number | null;
  assignedUserUid?: string[] | null;
  assignedInstrumentId?: number[] | null;
  assignedInstrumentIds?: number[] | null;
  couponCreatedBy?: string | null;
  couponCreatedAt?: string | null;
  couponUpdatedBy?: string | null;
  couponUpdatedAt?: string | null;
  codeCreatedBy?: string | null;
  codeCreatedAt?: string | null;
  codeUpdatedBy?: string | null;
  codeUpdatedAt?: string | null;
};

export type ValidatedCouponResponse = {
  couponId: string;
  couponCodeId: string;
  code: string;
  discountType: CouponDiscountType;
  percentOff: number | null;
  amountOffCents: number | null;
  minOrderAmountCents: number | null;
  assignedUserUid: string[] | null;
  couponStartsAt: string | null;
  couponEndsAt: string | null;
  codeStartsAt: string | null;
  codeEndsAt: string | null;
};

export type CartResponse = {
  id: number;
  status: "ACTIVE" | "COMPLETED" | "CANCELLED";
  items: CartItemResponse[];
  itemsCount: number;
  subtotalCents: number;
  totalDiscountCents: number;
  totalCents: number;
  progressiveSubtotalCents: number;
  progressiveDiscountCents: number;
  couponDiscountCents: number;
  appliedCoupon?: AppliedCouponResponse | null;
  updatedAt: string;
};

export class AnsweringClient {
  private readonly baseURL = environment.apiBaseUrl;

  constructor(private readonly requester = Axios) {}

  public authenticate(authenticationToken: string) {
    const path = "api/v1/users";
    const data = {
      authenticationToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data);
  }

  public refreshToken(refreshToken: string) {
    const path = "api/v1/users/refresh";
    const data = {
      refreshToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data);
  }

  public getManagedUsers(accessToken: string, params?: PaginationParams) {
    const path = "api/v1/admin/users/identities/search";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: {
        ...params,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getManagedAssignments(accessToken: string, params?: PaginationParams) {
    const path = "api/v1/admin/applications/search";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: {
        ...params,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public archiveApplication(accessToken: string, applicationId: number) {
    const path = `api/v1/admin/applications/${applicationId}`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.delete(fullPath, options);
  }

  public addAssignmentsToUser(
    accessToken: string,
    userUid: string,
    assignmentIds: number[]
  ) {
    const path = `api/v1/admin/users/${userUid}/assignments`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const data = {
      assignedApplications: assignmentIds,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public async getAnonAssignmentToken(
    accessToken: string,
    assignmentId: number
  ) {
    const path = `api/v1/admin/assignments/${assignmentId}/anonInviteToken`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return await this.requester.get(fullPath, options);
  }

  public async getGenericAnonAssignmentToken(
    accessToken: string,
    assignmentId: number
  ) {
    const path = `api/v1/admin/assignments/${assignmentId}/genericAnonInviteToken`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return await this.requester.get(fullPath, options);
  }

  public async getAssignmentToken(accessToken: string, assignmentId: number) {
    const path = `api/v1/admin/assignments/${assignmentId}/selfInviteToken`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return await this.requester.get(fullPath, options);
  }

  public inviteUser(
    accessToken: string,
    firstName: string,
    lastName: string,
    email: string,
    assignmentIds: number[],
    recaptchaToken: string
  ) {
    const path = "api/v1/admin/users/invite";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const data = {
      firstName,
      lastName,
      email: email.toLocaleLowerCase(),
      assignedApplications: assignmentIds,
      recaptchaToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public inviteAnonUser(
    email: string,
    accessToken: string,
    assignmentIds: number[],
    recaptchaToken: string
  ) {
    const path = "api/v1/admin/users/anon";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const data = {
      email,
      assignedApplications: assignmentIds,
      recaptchaToken,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public getManagedInstrumentsSearch(
    accessToken: string,
    params?: GetManagedInstrumentsSearchParams
  ) {
    const path = "api/v1/admin/instruments/search";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<List<IInstrument>>(fullPath, options);
  }

  public getInstrumentDiscounts(accessToken: string, instrumentId: number) {
    const path = `api/v1/admin/instruments/${instrumentId}/discounts`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<InstrumentDiscount[]>(fullPath, options);
  }

  public updateInstrumentDiscounts(
    accessToken: string,
    instrumentId: number,
    discounts: InstrumentDiscount[]
  ) {
    const path = `api/v1/admin/instruments/${instrumentId}/discounts`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.put(fullPath, discounts, options);
  }

  public getAllManagedInstruments(accessToken: string) {
    const path = "api/v1/admin/instruments";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<IInstrument[]>(fullPath, options);
  }

  public getAdminCart(accessToken: string, coupon?: string) {
    const path = "api/v1/admin/me/cart";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: coupon ? { coupon } : undefined,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<CartResponse>(fullPath, options);
  }

  public addCartItem(
    accessToken: string,
    payload: { instrumentId: number; quantity: number },
    coupon?: string
  ) {
    const path = "api/v1/admin/me/cart/items";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: coupon ? { coupon } : undefined,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, payload, options);
  }

  public updateCartItem(
    accessToken: string,
    cartItemId: number,
    payload: { quantity: number },
    coupon?: string
  ) {
    const path = `api/v1/admin/me/cart/items/${cartItemId}`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: coupon ? { coupon } : undefined,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.patch(fullPath, payload, options);
  }

  public deleteCartItem(accessToken: string, cartItemId: number, coupon?: string) {
    const path = `api/v1/admin/me/cart/items/${cartItemId}`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: coupon ? { coupon } : undefined,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.delete(fullPath, options);
  }

  public clearCart(accessToken: string) {
    const path = "api/v1/admin/me/cart/clear";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, undefined, options);
  }

  public validateCouponCode(
    accessToken: string,
    payload: { code: string; orderAmountCents: number; cartId: number }
  ) {
    const path = "api/v1/admin/coupon-codes/validate";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post<ValidatedCouponResponse>(
      fullPath,
      payload,
      options
    );
  }

  public createAssignment(
    accessToken: string,
    dueDate: Date,
    instrumentId: number,
    name: string
  ) {
    const path = "api/v1/admin/applications";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const data = {
      dueDate: dueDate.toISOString(),
      instrument: instrumentId,
      name,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public updateAssignmentDueDate(
    accessToken: string,
    applicationId: number,
    dueDate: Date
  ) {
    const path = `api/v1/admin/applications/${applicationId}/due-date`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const data = {
      dueDate: dueDate.toISOString(),
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.patch(fullPath, data, options);
  }

  public getAssignmentFeedbackToken(accessToken: string, assignmentId: number) {
    const path = `api/v1/admin/assignments/${assignmentId}/feedbackToken`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: {
        type: "applicator",
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getGroupFeedbackToken(accessToken: string, groupId: number) {
    const path = `api/v1/admin/groups/${groupId}/feedbackToken`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: {
        type: "applicator",
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public deleteAssignment(
    accessToken: string,
    userUid: string,
    assignmentId: number
  ) {
    const path = `api/v1/admin/users/${userUid}/assignments/${assignmentId}`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.delete(fullPath, options);
  }

  public getAddressData(accessToken: string, cep: string) {
    const path = `api/v1/admin/address/${cep}`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public getUserProfile(accessToken: string) {
    const path = "api/v1/admin/me/profile";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<UserProfile>(fullPath, options);
  }

  public updateUserProfile(
    accessToken: string,
    payload: UpdateUserProfilePayload
  ) {
    const path = "api/v1/admin/me/profile";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.put<UserProfile>(fullPath, payload, options);
  }

  public createPurchase(
    accessToken: string,
    cartId: number,
    user: ApiPurchaseUser,
    payment: ApiPurchasePayment,
    addressId: number,
    couponCode?: string
  ) {
    const path = `api/v1/admin/instruments/purchase`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: couponCode ? { coupon: couponCode } : undefined,
    };

    const fullPath = `${this.baseURL}${path}`;
    const data = {
      cartId,
      userData: user,
      paymentInfo: payment,
      addressId,
    };

    return this.requester.post(fullPath, data, options);
  }

  public addAppsToGroup(
    accessToken: string,
    appIds: number[],
    groupId: number
  ) {
    const path = "api/v1/groups/addtogroup";
    const data = {
        accessToken,
        appIds,
        groupId,
      },
      options = {
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public createGroup(accessToken: string, name: string, description?: string) {
    const path = "api/v1/groups";
    const data = {
      accessToken,
      name,
      description,
    };

    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public archiveGroup(authenticationToken: string, groupId?: string) {
    const path = "api/v1/groups";

    const fullPath = `${this.baseURL}${path}/${groupId}`;
    return this.requester.delete(fullPath, {
      headers: {
        authorization: `Bearer ${authenticationToken}`,
      },
    });
  }

  public getMyGroups(authenticationToken: string, params?: PaginationParams) {
    const path = "api/v1/groups/mine/search";

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, {
      headers: {
        authorization: `Bearer ${authenticationToken}`,
      },
      params: {
        ...params,
      },
    });
  }

  public getGroupInviteToken(authenticationToken: string, groupId: number) {
    const path = `api/v1/admin/groups/${groupId}/selfInviteToken`;

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<IGroupToken>(fullPath, {
      headers: {
        authorization: `Bearer ${authenticationToken}`,
      },
    });
  }

  /**
   * Solicita o ressarcimento de licenças para um lote de aplicação específico.
   * A API irá excluir todas as aplicações em andamento e devolver as licenças.
   * * @param {string} accessToken O token de acesso do usuário.
   * @param {number} applicationId O ID do lote da aplicação (`instrument_applications.id`).
   * @returns {Promise<any>} Uma promessa que resolve quando a operação é concluída.
   */
  public reimburseLicenses(
    accessToken: string,
    applicationId: number
  ): Promise<any> {
    const path = `api/v1/admin/applications/${applicationId}/reimburse`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, {}, options);
  }

  /**
   * Busca a lista de clientes de um revendedor.
   */
  public listResellerClients(accessToken: string, params?: getClientsParams) {
    const path = "api/v1/reseller/clients/search";
    const options = {
      headers: { authorization: `Bearer ${accessToken}` },
      params: {
        page: params?.page || 1,
        limit: params?.limit || 10,
        filter: params?.filter || undefined,
        origin: params?.origin || undefined,
        invitationStatus: params?.invitationStatus || undefined,
      },
    };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  /**
   * Distribui licenças de um instrumento para um cliente do revendedor.
   */
  public assignResellerLicenses(
    accessToken: string,
    clientId: number,
    items: LicenseAssignmentItem[]
  ) {
    const path = `api/v1/reseller/licenses/clients/${clientId}/assign`;
    const data = { items };
    const options = {
      headers: { authorization: `Bearer ${accessToken}` },
    };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  /**
   * Adiciona ou convida um novo cliente para a carteira do revendedor.
   */
  public addOrInviteResellerClient(
    accessToken: string,
    data: IAddOrInviteResellerClientParams
  ) {
    const path = "api/v1/reseller/clients";
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  /**
   * Revendedor solicita ao administrador a promoção de um de seus clientes a psicólogo.
   */
  public requestPsychologistPromotion(
    accessToken: string,
    params: RequestPsychologistPromotionParams
  ) {
    const { clientUid, ...data } = params;
    const path = `api/v1/reseller/clients/${clientUid}/request-psychologist-promotion`;
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, data, options);
  }

  public resendResellerInvitation(accessToken: string, clientUid: string) {
    const path = `api/v1/reseller/clients/${clientUid}/resend-invitation`;
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, {}, options);
  }

  /**
   * Busca o histórico de licenças distribuídas por um revendedor.
   */
  public getResellerLicenseHistory(
    accessToken: string,
    params?: GetLicenseHistoryParams
  ) {
    const path = "api/v1/reseller/licenses/history/search";
    const options = {
      headers: { authorization: `Bearer ${accessToken}` },
      params,
    };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  public promoteToReseller(accessToken: string, userUid: string) {
    console.log("Promoting user to reseller:", userUid);
    const path = `api/v1/admin/users/${userUid}/promote-to-reseller`;
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.patch(fullPath, {}, options);
  }

  /**
   * Promove um usuário para psicólogo diretamente pela rota de administrador.
   * @param accessToken O token de acesso do administrador.
   * @param userUid O UID do usuário a ser promovido.
   * @param dto Os dados necessários para a promoção (PromoteToPsychologistDto).
   */
  public promoteToPsychologist(
    accessToken: string,
    userUid: string,
    dto: PromoteToPsychologistParams
  ) {
    const path = `api/v1/admin/users/${userUid}/promote-to-psychologist`;
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.post(fullPath, dto, options);
  }

  /**
   * Busca todos os usuários da plataforma com paginação e filtros.
   * Usado pela visão de administrador para ver todos os usuários.
   * @param accessToken O token de acesso do admin.
   * @param params Parâmetros de busca (página, filtro, etc.).
   */
  public searchAllUsers(accessToken: string, params?: SearchAllUsersParams) {
    const path = "api/v1/admin/users/search";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params: params,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  /**
   * Busca por usuários com a role de Revendedor e dados agregados.
   * @param accessToken Token do admin.
   * @param params Parâmetros de busca (página, filtro).
   */
  public searchResellers(accessToken: string, params?: SearchAllUsersParams) {
    const path = "api/v1/admin/resellers/search";
    const options = {
      headers: { authorization: `Bearer ${accessToken}` },
      params: params,
    };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  /**
   * Busca a análise de impacto antes de remover um revendedor.
   */
  public getDemotionImpact(accessToken: string, resellerUid: string) {
    const path = `api/v1/admin/resellers/${resellerUid}/demotion-impact`;
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get(fullPath, options);
  }

  /**
   * Executa a remoção do cargo de revendedor.
   */
  public demoteReseller(accessToken: string, resellerUid: string) {
    const path = `api/v1/admin/resellers/${resellerUid}/demote`;
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.patch(fullPath, {}, options);
  }

  /**
   * Busca os instrumentos que um revendedor pode distribuir.
   */
  public searchResellerInstruments(
    accessToken: string,
    params?: GetManagedInstrumentsSearchParams
  ) {
    const path = "api/v1/reseller/instruments/search";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params,
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<List<IInstrument>>(fullPath, options);
  }

  /**
   * Busca mapa de traduções de roles (pt-br) a partir do instruments-service.
   * Exige header Authorization: Bearer <AccessToken>.
   */
  public getRoleTranslations(accessToken: string) {
    const path = "api/v1/roles/translations/pt-br";
    const options = {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<RoleTranslations>(fullPath, options);
  }

  /**
   * Revoga uma quantidade de licenças de um lote distribuído.
   */
  public revokeResellerLicenses(
    accessToken: string,
    distributedLicenseId: number,
    quantityToRevoke: number
  ) {
    const path = `api/v1/reseller/licenses/${distributedLicenseId}/revoke`;
    const data = { quantityToRevoke };
    const options = { headers: { authorization: `Bearer ${accessToken}` } };
    const fullPath = `${this.baseURL}${path}`;
    return this.requester.patch(fullPath, data, options);
  }

  /**
   * Lista eventos de ASSIGN/REVOKE do reseller autenticado (paginado).
   * GET api/v1/reseller/licenses/events/search
   */
  public getResellerLicenseEvents(
    accessToken: string,
    params?: GetLicenseEventsParams
  ) {
    const path = "api/v1/reseller/licenses/events/search";

    // normaliza datas (Date -> ISO) e aplica defaults simples
    const p = params ?? {};
    const toIso = (d?: Date | string) =>
      d instanceof Date ? d.toISOString() : d;

    const options = {
      headers: { authorization: `Bearer ${accessToken}` },
      params: {
        page: p.page ?? 1,
        limit: p.limit ?? 15,
        order: p.order ?? "desc",
        clientId: p.clientId,
        instrumentId: p.instrumentId,
        from: toIso(p.from),
        to: toIso(p.to),
      },
    };

    const fullPath = `${this.baseURL}${path}`;
    return this.requester.get<List<LicenseEventDTO>>(fullPath, options);
  }
}
